/**
 * useDebugSession
 *
 * Hook que carrega o histórico completo de uma sessão (events.jsonl)
 * e, se a sessão estiver ativa, conecta ao SSE para receber novos eventos
 * em tempo real.
 *
 * Adaptado de useVMSession.ts do hub-studio-web, simplificado:
 *  - Não persiste em banco (events.jsonl é a fonte de verdade)
 *  - Auth via cookie (não passa secret)
 *  - Reconstrói chat unificado a partir de eventos do tipo:
 *      user_message, assistant_message_*, tool_use, tool_result, error
 *  - Mantém audit turns separadamente para o modo debug
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { SessionDetail, SessionEvent, SessionStatus } from '../types';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string;
  ts: string;
  isStreaming?: boolean;
  toolName?: string;
  toolInput?: unknown;
  toolOutput?: unknown;
  toolError?: boolean;
}

export interface TurnAudit {
  ts: string;
  turnNumber: number;
  provider: string;
  model: string;
  systemPrompt: string;
  systemPromptTruncated: boolean;
  history: Array<{ role: string; text: string; truncated: boolean; contentType: string }>;
  responseText: string;
  responseTruncated: boolean;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  costUsd: number;
  toolsUsed: string[];
  modePromptVersion?: number | null;
  commonAppendVersion?: number | null;
  toolContractVersion?: number | null;
}

interface UseDebugSessionReturn {
  detail: SessionDetail | null;
  messages: ChatMessage[];
  audits: TurnAudit[];
  loading: boolean;
  error: string | null;
  isLive: boolean;
  status: SessionStatus | null;
  reload: () => void;
}

const ACTIVE_STATUSES: SessionStatus[] = ['initializing', 'ready', 'running', 'paused'];

/** Reduz array de eventos a (mensagens do chat, audits, status atual) */
function reduceEvents(events: SessionEvent[]): {
  messages: ChatMessage[];
  audits: TurnAudit[];
  status: SessionStatus | null;
} {
  const messages: ChatMessage[] = [];
  const audits: TurnAudit[] = [];
  let status: SessionStatus | null = null;

  // mapa para acumular mensagens em streaming
  const streaming = new Map<string, ChatMessage>();
  // mapa para tool_use_id → mensagem do tool
  const toolMessages = new Map<string, ChatMessage>();

  for (const ev of events) {
    switch (ev.type) {
      case 'session_created':
        status = 'initializing';
        break;

      case 'status_change':
        status = ev.to;
        break;

      case 'user_message':
        messages.push({
          id: `user-${ev.ts}-${messages.length}`,
          role: 'user',
          content: ev.content,
          ts: ev.ts,
        });
        break;

      case 'assistant_message_start': {
        const msg: ChatMessage = {
          id: ev.messageId,
          role: 'assistant',
          content: '',
          ts: ev.ts,
          isStreaming: true,
        };
        streaming.set(ev.messageId, msg);
        messages.push(msg);
        break;
      }

      case 'assistant_message_delta': {
        const msg = streaming.get(ev.messageId);
        if (msg) {
          msg.content += ev.text;
        } else {
          // delta sem start → cria sob demanda
          const newMsg: ChatMessage = {
            id: ev.messageId,
            role: 'assistant',
            content: ev.text,
            ts: ev.ts,
            isStreaming: true,
          };
          streaming.set(ev.messageId, newMsg);
          messages.push(newMsg);
        }
        break;
      }

      case 'assistant_message_done': {
        const msg = streaming.get(ev.messageId);
        if (msg) {
          msg.content = ev.finalText || msg.content;
          msg.isStreaming = false;
          streaming.delete(ev.messageId);
        } else {
          messages.push({
            id: ev.messageId,
            role: 'assistant',
            content: ev.finalText,
            ts: ev.ts,
            isStreaming: false,
          });
        }
        break;
      }

      case 'tool_use': {
        const msg: ChatMessage = {
          id: `tool-${ev.toolUseId}`,
          role: 'tool',
          content: '',
          ts: ev.ts,
          toolName: ev.name,
          toolInput: ev.input,
        };
        toolMessages.set(ev.toolUseId, msg);
        messages.push(msg);
        break;
      }

      case 'tool_result': {
        const msg = toolMessages.get(ev.toolUseId);
        if (msg) {
          msg.toolOutput = ev.output;
          msg.toolError = ev.isError;
        }
        break;
      }

      case 'error':
        messages.push({
          id: `error-${ev.ts}-${messages.length}`,
          role: 'system',
          content: `❌ ${ev.message}`,
          ts: ev.ts,
        });
        break;

      case 'turn_audit':
        audits.push({
          ts: ev.ts,
          turnNumber: ev.turnNumber,
          provider: ev.provider,
          model: ev.model,
          systemPrompt: ev.systemPrompt,
          systemPromptTruncated: ev.systemPromptTruncated,
          history: ev.history,
          responseText: ev.responseText,
          responseTruncated: ev.responseTruncated,
          inputTokens: ev.inputTokens,
          outputTokens: ev.outputTokens,
          cachedTokens: ev.cachedTokens,
          costUsd: ev.costUsd,
          toolsUsed: ev.toolsUsed,
          modePromptVersion: ev.modePromptVersion,
          commonAppendVersion: ev.commonAppendVersion,
          toolContractVersion: ev.toolContractVersion,
        });
        break;

      default:
        break;
    }
  }

  return { messages, audits, status };
}

export function useDebugSession(sessionId: string | null): UseDebugSessionReturn {
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [liveStatus, setLiveStatus] = useState<SessionStatus | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Carga inicial: detail + events
  useEffect(() => {
    if (!sessionId) {
      setDetail(null);
      setEvents([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([api.getSession(sessionId), api.getEvents(sessionId, { limit: 5000 })])
      .then(([d, e]) => {
        if (cancelled) return;
        setDetail(d);
        setEvents(e.events);
        setLiveStatus(d.live?.status ?? d.meta.status);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId, reloadKey]);

  // Conexão SSE se ativa
  useEffect(() => {
    if (!sessionId || !detail) return;
    const status = detail.live?.status ?? detail.meta.status;
    if (!ACTIVE_STATUSES.includes(status)) return;

    const url = `/api/debug/sessions/${sessionId}/stream`;
    const es = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = es;

    es.onmessage = (msg) => {
      try {
        const parsed = JSON.parse(msg.data) as { type: string; data: unknown; timestamp?: string };
        // Reconstrói SessionEvent a partir do SSE event quando possível
        // Para a maioria, recarregamos os eventos do servidor para garantir consistência
        // Aqui só atualizamos status do heartbeat
        if (parsed.type === 'heartbeat') {
          const data = parsed.data as { status?: SessionStatus };
          if (data?.status) setLiveStatus(data.status);
        } else {
          // Trigger reload ocasional para puxar novos eventos persistidos
          // (alternativa simples: recarrega events do servidor a cada SSE event)
          api
            .getEvents(sessionId, { limit: 5000 })
            .then((e) => setEvents(e.events))
            .catch(() => undefined);
        }
      } catch {
        // ignore parse errors
      }
    };

    es.onerror = () => {
      // EventSource reconecta automaticamente
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [sessionId, detail]);

  const reduced = reduceEvents(events);
  const isLive = ACTIVE_STATUSES.includes(liveStatus ?? ('stopped' as SessionStatus));

  return {
    detail,
    messages: reduced.messages,
    audits: reduced.audits,
    loading,
    error,
    isLive,
    status: liveStatus ?? reduced.status,
    reload,
  };
}
